import React, { useState, useEffect, useCallback } from 'react';
import {
    View, Text, StyleSheet, FlatList, TouchableOpacity,
    Alert, RefreshControl, ActivityIndicator, Image, Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getStudentBookings, getMentorBookings, updateBookingStatus } from '../../api/mentor';
import { useAuth } from '../../contexts/AuthContext';
import { getShadow } from '../../theme/useTheme';
import { H2, Body1, Body2, Caption } from '../../components/common/TextStyles';

const MENTOR_PRIMARY = '#7c3aed';

const MyBookingsScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const { user } = useAuth();
    const isMentor = user?.isMentor;

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('student'); // 'student' or 'mentor'

    const fetchBookings = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            let data = [];
            if (activeTab === 'student') {
                data = await getStudentBookings();
            } else {
                data = await getMentorBookings();
            }
            setBookings(data);
        } catch (error) {
            console.error('Fetch Bookings Error:', error);
            Alert.alert('Error', 'Failed to load your mentor sessions.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchBookings(false);
    };

    const handleUpdateStatus = async (bookingId, newStatus) => {
        try {
            const actionText = newStatus === 'confirmed' ? 'confirm' : 'cancel';
            Alert.alert(
                `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Session`,
                `Are you sure you want to ${actionText} this session?`,
                [
                    { text: 'No', style: 'cancel' },
                    {
                        text: 'Yes',
                        onPress: async () => {
                            try {
                                await updateBookingStatus(bookingId, newStatus);
                                Alert.alert('Success', `Session ${newStatus} successfully.`);
                                fetchBookings(false);
                            } catch (err) {
                                Alert.alert('Error', err || 'Failed to update status.');
                            }
                        }
                    }
                ]
            );
        } catch (error) {
            console.error('Update Status Error:', error);
        }
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'confirmed':
                return { label: 'CONFIRMED', color: '#10B981', bg: '#10B98115', icon: 'checkmark-circle' };
            case 'pending':
                return { label: 'PENDING', color: '#F59E0B', bg: '#F59E0B15', icon: 'time' };
            case 'cancelled':
                return { label: 'CANCELLED', color: '#EF4444', bg: '#EF444415', icon: 'close-circle' };
            case 'completed':
                return { label: 'COMPLETED', color: '#6366F1', bg: '#6366F115', icon: 'ribbon' };
            default:
                return { label: status?.toUpperCase(), color: '#6B7280', bg: '#F3F4F6', icon: 'help-circle' };
        }
    };

    const renderBooking = ({ item }) => {
        const config = getStatusConfig(item.status);
        const otherUser = activeTab === 'student' ? item.mentor : item.student;
        const date = new Date(item.scheduledAt);
        const formattedDate = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        return (
            <View style={styles.bookingCard}>
                <View style={styles.cardHeader}>
                    <Image
                        source={{ uri: otherUser?.profileImageUrl || 'https://i.pravatar.cc/100' }}
                        style={styles.avatar}
                    />
                    <View style={styles.userInfo}>
                        <Text style={styles.userName}>{otherUser?.name || 'User'}</Text>
                        <Text style={styles.userRole}>
                            {activeTab === 'student' ? (otherUser?.mentorType || 'Mentor') : 'Student'}
                        </Text>
                    </View>
                    <View style={[styles.statusBadge, { backgroundColor: config.bg }]}>
                        <Ionicons name={config.icon} size={12} color={config.color} style={{ marginRight: 4 }} />
                        <Text style={[styles.statusText, { color: config.color }]}>{config.label}</Text>
                    </View>
                </View>

                <View style={styles.cardDivider} />

                <View style={styles.sessionDetails}>
                    <View style={styles.detailRow}>
                        <Ionicons name="calendar-outline" size={16} color="#6B7280" />
                        <Text style={styles.detailText}>{formattedDate} • {formattedTime}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Ionicons name="videocam-outline" size={16} color="#6B7280" />
                        <Text style={styles.detailText}>{item.sessionType?.replace('-', ' ')} • {item.duration}m</Text>
                    </View>
                    {item.message && (
                        <View style={styles.messageBox}>
                            <Text style={styles.messageLabel}>Message:</Text>
                            <Text style={styles.messageText} numberOfLines={2}>{item.message}</Text>
                        </View>
                    )}
                </View>

                {item.status === 'pending' && (
                    <View style={styles.actionRow}>
                        {activeTab === 'mentor' && (
                            <TouchableOpacity
                                style={[styles.actionBtn, styles.confirmBtn]}
                                onPress={() => handleUpdateStatus(item.id, 'confirmed')}
                            >
                                <Text style={styles.confirmBtnText}>Confirm</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            style={[styles.actionBtn, styles.cancelBtn]}
                            onPress={() => handleUpdateStatus(item.id, 'cancelled')}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                )}
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                    <Ionicons name="arrow-back" size={24} color="#1F2937" />
                </TouchableOpacity>
                <H2 style={styles.headerTitle}>My Sessions</H2>
                <View style={{ width: 40 }} />
            </View>

            {isMentor && (
                <View style={styles.tabBar}>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'student' && styles.activeTab]}
                        onPress={() => setActiveTab('student')}
                    >
                        <Text style={[styles.tabText, activeTab === 'student' && styles.activeTabText]}>AS STUDENT</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'mentor' && styles.activeTab]}
                        onPress={() => setActiveTab('mentor')}
                    >
                        <Text style={[styles.tabText, activeTab === 'mentor' && styles.activeTabText]}>AS MENTOR</Text>
                    </TouchableOpacity>
                </View>
            )}

            {loading && !refreshing ? (
                <View style={styles.centerContainer}>
                    <ActivityIndicator size="large" color={MENTOR_PRIMARY} />
                </View>
            ) : (
                <FlatList
                    data={bookings}
                    renderItem={renderBooking}
                    keyExtractor={item => item.id}
                    contentContainerStyle={[styles.listContent, { paddingBottom: insets.bottom + 20 }]}
                    refreshControl={
                        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={MENTOR_PRIMARY} />
                    }
                    ListEmptyComponent={
                        <View style={styles.emptyState}>
                            <View style={styles.emptyIconWrap}>
                                <Ionicons name="calendar-outline" size={64} color="#D1D5DB" />
                            </View>
                            <Text style={styles.emptyTitle}>No sessions yet</Text>
                            <Text style={styles.emptySubtitle}>
                                {activeTab === 'student' 
                                    ? "You haven't requested any sessions with mentors yet."
                                    : "You haven't received any session requests yet."
                                }
                            </Text>
                            {activeTab === 'student' && (
                                <TouchableOpacity 
                                    style={styles.browseBtn}
                                    onPress={() => navigation.navigate('MentorHub')}
                                >
                                    <Text style={styles.browseBtnText}>Find a Mentor</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    }
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingBottom: 15,
        backgroundColor: '#FFFFFF',
        ...getShadow('sm'),
    },
    backBtn: {
        padding: 5,
    },
    headerTitle: {
        fontSize: 20,
    },
    tabBar: {
        flexDirection: 'row',
        backgroundColor: '#FFFFFF',
        marginTop: 2,
    },
    tab: {
        flex: 1,
        paddingVertical: 15,
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
    },
    activeTab: {
        borderBottomColor: MENTOR_PRIMARY,
    },
    tabText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#6B7280',
    },
    activeTabText: {
        color: MENTOR_PRIMARY,
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    listContent: {
        padding: 16,
    },
    bookingCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 16,
        marginBottom: 16,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    avatar: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#F3F4F6',
    },
    userInfo: {
        flex: 1,
        marginLeft: 12,
    },
    userName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
    },
    userRole: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: 2,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 12,
    },
    statusText: {
        fontSize: 11,
        fontWeight: '800',
    },
    cardDivider: {
        height: 1,
        backgroundColor: '#F3F4F6',
        marginVertical: 15,
    },
    sessionDetails: {
        gap: 8,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    detailText: {
        fontSize: 14,
        color: '#4B5563',
        fontWeight: '500',
    },
    messageBox: {
        backgroundColor: '#F3F4F6',
        padding: 10,
        borderRadius: 12,
        marginTop: 5,
    },
    messageLabel: {
        fontSize: 11,
        fontWeight: '700',
        color: '#6B7280',
        marginBottom: 2,
    },
    messageText: {
        fontSize: 13,
        color: '#374151',
        fontStyle: 'italic',
    },
    actionRow: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 20,
    },
    actionBtn: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmBtn: {
        backgroundColor: MENTOR_PRIMARY,
    },
    confirmBtnText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 14,
    },
    cancelBtn: {
        backgroundColor: '#FFF',
        borderWidth: 1.5,
        borderColor: '#EF444430',
    },
    cancelBtnText: {
        color: '#EF4444',
        fontWeight: '700',
        fontSize: 14,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 60,
    },
    emptyIconWrap: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    emptySubtitle: {
        fontSize: 14,
        color: '#6B7280',
        textAlign: 'center',
        paddingHorizontal: 40,
        marginBottom: 25,
        lineHeight: 20,
    },
    browseBtn: {
        backgroundColor: MENTOR_PRIMARY,
        paddingHorizontal: 25,
        paddingVertical: 14,
        borderRadius: 16,
    },
    browseBtnText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 15,
    }
});

export default MyBookingsScreen;
